// components/StoreSelector.js
import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Select, MenuItem, FormControl, Paper, Icon } from "@mui/material";
import { styled } from "@mui/system";
import { Store as StoreIcon } from "@mui/icons-material";
import theme from "assets/theme";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  display: "flex",
  alignItems: "center",
  transition: "transform 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-2px)",
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: 240,
  marginLeft: theme.spacing(2),
  "& .MuiSelect-select": {
    ...theme.components.MuiSelect.styleOverrides.select,
    borderRadius: theme.shape.borderRadius * 2,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    padding: theme.spacing(1.5, 2),
    height: theme.spacing(7),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
    },
  },
  "& .MuiSelect-icon": {
    ...theme.components.MuiSelect.styleOverrides.icon,
    right: theme.spacing(1),
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  ...theme.components.MuiMenuItem.styleOverrides,
  padding: theme.spacing(1.5, 2),
  minHeight: theme.spacing(6),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StoreSelector = ({ stores, selectedStore, onStoreChange }) => {
  const handleChange = (event) => {
    onStoreChange(event.target.value);
  };

  return (
    <StyledPaper>
      <Box display="flex" alignItems="center">
        <Icon color="primary" sx={{ fontSize: 24, marginRight: 1 }}>
          <StoreIcon />
        </Icon>
        <Typography variant="h6" color="primary" fontWeight="bold">
          Select Store:
        </Typography>
        <StyledFormControl variant="outlined">
          <Select value={selectedStore} onChange={handleChange} displayEmpty>
            <StyledMenuItem value="" disabled>
              Choose a store...
            </StyledMenuItem>
            {stores.map((store) => (
              <StyledMenuItem key={store.id} value={store.id}>
                {store.name}
              </StyledMenuItem>
            ))}
          </Select>
        </StyledFormControl>
      </Box>
    </StyledPaper>
  );
};

StoreSelector.propTypes = {
  stores: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedStore: PropTypes.string.isRequired,
  onStoreChange: PropTypes.func.isRequired,
};

export default StoreSelector;
